import { useEffect, useState } from 'react'

import { ApiError, api, can, type ConnectorSending, type MessageKind, type MessagingPolicy } from '../../lib/api'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status, Toggletip } from '../../components/kit'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import { useToast } from '../../components/ui'
import '../../styles/sweep.css'

// Reaching a vendor who does not answer email.
//
// # WHAT THIS SCREEN DECIDES
//
// The order a questionnaire reminder tries the channels in, how long each is
// given before the next, when the first reminder goes, and how long a vendor
// can go unreached before it is raised on the risk register. The carriers
// themselves are cards on the Integrations tab; this is the policy that uses
// them.
//
// Each field keeps one line saying what it does; what follows from it opens
// behind the (i), so the four numbers can be read down the page at a glance.

const CHANNEL_WORD: Record<string, string> = { EMAIL: 'Email', WHATSAPP: 'WhatsApp', SMS: 'SMS' }
const ALL = ['EMAIL', 'WHATSAPP', 'SMS']

export function VendorMessaging() {
  const toast = useToast()
  const [policy, setPolicy] = useState<MessagingPolicy | null>(null)
  const [saved, setSaved] = useState<MessagingPolicy | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')
  const [carriers, setCarriers] = useState<{ name: string; sending: ConnectorSending; connected: boolean }[]>([])
  const [kinds, setKinds] = useState<MessageKind[]>([])

  useEffect(() => {
    api
      .messagingPolicy()
      .then((r) => {
        setPolicy(r.policy)
        setSaved(r.policy)
      })
      .catch(setError)
    // Secondary to the policy: a failure to read them leaves the screen usable.
    api
      .connectors()
      .then((v) => {
        const out: { name: string; sending: ConnectorSending; connected: boolean }[] = []
        for (const g of v.groups) {
          for (const i of g.items) {
            if (i.sending) out.push({ name: i.template.displayName, sending: i.sending, connected: Boolean(i.instance) })
          }
        }
        setCarriers(out)
      })
      .catch(() => setCarriers([]))
    api
      .messagingKinds()
      .then((r) => setKinds(r.kinds))
      .catch(() => setKinds([]))
  }, [])

  if (error != null) return <ErrorBox error={error} what="Reaching vendors" />
  if (!policy || !saved) return <Loading what="the messaging policy" />

  const mayWrite = can('programme.write')
  const dirty = JSON.stringify(policy) !== JSON.stringify(saved)
  const unused = ALL.filter((c) => !policy.reminderLadder.includes(c))

  function move(i: number, by: number) {
    if (!policy) return
    const next = [...policy.reminderLadder]
    const j = i + by
    if (j < 0 || j >= next.length) return
    const moved = next[i] as string
    next[i] = next[j] as string
    next[j] = moved
    setPolicy({ ...policy, reminderLadder: next })
  }

  async function save() {
    if (!policy) return
    setBusy(true)
    setProblem('')
    try {
      const r = await api.saveMessagingPolicy(policy)
      setPolicy(r.policy)
      setSaved(r.policy)
      toast(r.note)
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="settings-stack">
      <section className="panel">
        <div className="p1-head">
          <div>
            <h2>Reaching vendors</h2>
            <p className="obs-what">How a questionnaire reminder finds a vendor who has not answered</p>
          </div>
        </div>

        <div className="field-block">
          <span className="field-label">Try in this order</span>
          <ol className="p1-inline-list">
            {policy.reminderLadder.map((c, i) => (
              <li key={c}>
                {CHANNEL_WORD[c] ?? c}{' '}
                {mayWrite && (
                  <span className="chiprow">
                    <button type="button" className="linkish" disabled={i === 0} onClick={() => move(i, -1)}>
                      Earlier
                    </button>
                    <button type="button" className="linkish" disabled={i === policy.reminderLadder.length - 1} onClick={() => move(i, 1)}>
                      Later
                    </button>
                    {policy.reminderLadder.length > 1 && (
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => setPolicy({ ...policy, reminderLadder: policy.reminderLadder.filter((x) => x !== c) })}
                      >
                        Remove
                      </button>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ol>
          {mayWrite &&
            unused.map((c) => (
              <button
                key={c}
                type="button"
                className="linkish"
                onClick={() => setPolicy({ ...policy, reminderLadder: [...policy.reminderLadder, c] })}
              >
                Add {CHANNEL_WORD[c]}
              </button>
            ))}
          <span className="field-note">
            A contact's preferred channel goes first{' '}
            <Toggletip label="Channels a contact is not sent">
              WhatsApp is skipped for a contact with no recorded agreement, and any channel a contact objected to is never
              used.
            </Toggletip>
          </span>
        </div>

        <div className="field-row two">
          <label>
            Wait before the next channel, in hours
            <input
              type="number"
              min={1}
              max={336}
              value={Math.round(policy.rungWaitMinutes / 60)}
              onChange={(e) => setPolicy({ ...policy, rungWaitMinutes: Math.max(1, Number(e.target.value)) * 60 })}
              disabled={!mayWrite}
            />
            <span className="field-note">A message confirmed as delivered, or the vendor answering, stops the reminder</span>
          </label>
          <label>
            First reminder, days before the due date
            <input
              type="number"
              min={0}
              max={60}
              value={policy.reminderDaysBeforeDue}
              onChange={(e) => setPolicy({ ...policy, reminderDaysBeforeDue: Number(e.target.value) })}
              disabled={!mayWrite}
            />
            <span className="field-note">A second reminder goes the day after the due date if nothing has come back</span>
          </label>
        </div>

        <div className="field-row two">
          <label>
            Raise as unreachable after, in hours
            <input
              type="number"
              min={1}
              max={2160}
              value={policy.unreachableAfterHours}
              onChange={(e) => setPolicy({ ...policy, unreachableAfterHours: Number(e.target.value) })}
              disabled={!mayWrite}
            />
            <span className="field-note">
              A finding is raised when nothing reaches the vendor in this time{' '}
              <Toggletip label="When the finding opens and closes">
                When every channel fails, or nothing reaches any of the vendor's contacts in this time, a finding is raised
                on the vendor. It closes on its own when a later message is delivered.
              </Toggletip>
            </span>
          </label>
        </div>

        {problem && <p className="note stop">{problem}</p>}
        {mayWrite && (
          <button
            type="button"
            className="button primary"
            disabled={busy || !dirty}
            onClick={() => void save()}
            data-action="tprm.settings-messaging.a01"
          >
            {busy ? 'Saving' : 'Save'}
          </button>
        )}
      </section>

      <section className="panel">
        <h3 style={{ marginTop: 0 }}>Carriers</h3>
        <p className="obs-what">
          Carriers send only once a test message is delivered, test them on{' '}
          <a href="#/settings?t=integrations">Integrations</a>
        </p>
        {/* One label per carrier. The API's reason a connected carrier cannot
            send yet is its own sentence, so it opens beside the label. */}
        <ul className="p1-inline-list">
          {carriers.map((c) => (
            <li key={c.name}>
              {/* Inline, so the row sits on the bullet's line. */}
              <span className="sw-row" style={{ display: 'inline-flex' }}>
                <strong>{c.name}</strong>
                {c.sending.probeOnly ? (
                  <Status tone="neutral" title="Checks a key only, and sends nothing">
                    Check only
                  </Status>
                ) : !c.connected ? (
                  <Status tone="neutral">Not connected</Status>
                ) : c.sending.canSend ? (
                  <Status tone="ok">Can send</Status>
                ) : (
                  <>
                    <Status tone="warn">Cannot send</Status>
                    <ReasonTip label={'Why ' + c.name + ' cannot send yet'} text={c.sending.why} />
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {kinds.length > 0 && (
        <section className="panel">
          <h3 style={{ marginTop: 0 }}>Wording to register</h3>
          <p className="obs-what">
            Register each exactly as shown, then enter its name or id on the carrier's card{' '}
            <Toggletip label="Why wording is registered">
              WhatsApp and Indian SMS carry only wording registered in advance.
            </Toggletip>
          </p>
          {kinds.map((k) => (
            <div key={k.key} className="field-block">
              <span className="field-label">{k.label}</span>
              <span className="field-note">
                WhatsApp, {k.whatsAppCategory} template: <span className="mono">{k.whatsAppBody}</span>
              </span>
              <span className="field-note">
                SMS, DLT template: <span className="mono">{k.smsText}</span>
              </span>
              <span className="field-note">Values in order: {k.params.join(', ')}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}
